"use client";
import { Link } from "@/components/CustomLink";
import { ROUTES } from "@/@core/constants/routes";
import CalendarDaysIcon from "@heroicons/react/24/outline/CalendarDaysIcon";
import TicketIcon from "@heroicons/react/24/outline/TicketIcon";
import { useSite } from "@/lib/hooks/site";
import { useTranslation } from "@/hooks/translationContext";
import ReceiptPercentIcon from "@heroicons/react/24/outline/ReceiptPercentIcon";
import BuildingStorefrontIcon from "@heroicons/react/24/outline/BuildingStorefrontIcon";
import MagnifyingGlassIcon from "@heroicons/react/24/outline/MagnifyingGlassIcon";
import { useSearchContext } from "@/lib/hooks/searchContext/searchStore";

export type HeroButtonType = 'companyDiscounts' | 'campaign' | 'event' | 'venue' | 'search';
export type HeroButtonVariant = 'default' | 'stat';

export default function HeroButton({ type, count, variant = 'default' }: { type: HeroButtonType, count?: number, variant?: HeroButtonVariant }) {
  const site = useSite();
  const { t } = useTranslation();
  const { setIsOpen } = useSearchContext();

  const campaignTitle = t("text-menu-content-campaign", { defaultValue: "Kampanyalar" });
  const eventTitle = t("text-menu-content-event", { defaultValue: "Etkinlikler" });
  const venueTitle = t("venue-header-title", { defaultValue: "Mekanlar" });
  const searchTitle = t("text-search", { defaultValue: "Ara" });

  const buttonTypes = [
    {
      key: 'venue',
      icon: BuildingStorefrontIcon,
      href: `/${site?.setting?.routes?.generalList ?? ""}`,
      isVisible: true,
      enabled: true,
      title: venueTitle,
      showCount: true,
    },
    {
      key: 'search',
      icon: MagnifyingGlassIcon,
      isVisible: true,
      enabled: true,
      title: searchTitle,
      onClick: () => setIsOpen(true),
      showCount: false,
    },
    {
      key: 'campaign',
      icon: TicketIcon,
      href: ROUTES.CAMPAIGNS,
      isVisible: site?.setting?.disabledPages?.campaign !== 1,
      enabled: site?.setting?.pageButtons?.homepage?.includes("campaign"),
      title: campaignTitle,
      showCount: true,
    },
    {
      key: 'event',
      icon: CalendarDaysIcon,
      href: ROUTES.EVENTS,
      isVisible: site?.setting?.disabledPages?.event !== 1,
      enabled: site?.setting?.pageButtons?.homepage?.includes("event"),
      title: eventTitle,
      showCount: true,
    },
    {
      key: 'companyDiscounts',
      icon: ReceiptPercentIcon,
      href: ROUTES.ALL_COMPANIES,
      isVisible: site?.setting?.disabledPages?.company !== 1,
      enabled: site?.setting?.pageButtons?.homepage?.includes("companyDiscounts"),
      title: 'Şirket İndirimleri',
      showCount: true,
    },
  ];

  const currentButton = buttonTypes.find(button => button.key === type);

  if (!currentButton || !currentButton.isVisible) {
    return null;
  }

  if (
    currentButton.key !== 'search' &&
    currentButton.key !== 'venue' &&
    Array.isArray(site?.setting?.pageButtons?.homepage) &&
    !currentButton.enabled
  ) {
    return null;
  }

  if (
    currentButton.showCount &&
    currentButton.key !== "venue" &&
    (count === 0 || count == null)
  ) {
    return null;
  }

  const Icon = currentButton.icon;

  if (variant === 'stat') {
    const statContent = (
      <div className="flex items-center gap-[clamp(0.375rem,0.8vw,0.625rem)] cursor-pointer min-w-0">
        <Icon
          className="size-[clamp(1.25rem,1.6vw,1.75rem)] text-primary shrink-0"
          strokeWidth={1.5}
        />
        <div className="flex flex-col leading-tight min-w-0">
          {currentButton.showCount && count != null && count > 0 ? (
            <span className="text-[clamp(0.875rem,0.9vw,1rem)] font-bold text-primary leading-none">{count}</span>
          ) : (
            <span aria-hidden className="text-[clamp(0.875rem,0.9vw,1rem)] font-bold leading-none opacity-0 select-none">0</span>
          )}
          <span className="text-[clamp(0.65rem,0.8vw,0.75rem)] font-medium text-gray-500 truncate">
            {currentButton.title}
          </span>
        </div>
      </div>
    );

    if (currentButton.onClick) {
      return (
        <button
          type="button"
          onClick={currentButton.onClick}
          className="flex px-[clamp(0.5rem,1vw,1rem)] first:pl-0 last:pr-0 justify-center first:justify-start last:justify-end"
        >
          {statContent}
        </button>
      );
    }

    return (
      <Link
        href={currentButton.href as string}
        className="flex px-[clamp(0.5rem,1vw,1rem)] first:pl-0 last:pr-0 justify-center first:justify-start last:justify-end"
      >
        {statContent}
      </Link>
    );
  }

  const content = (
    <div className="flex flex-col items-center justify-center gap-1 cursor-pointer min-w-[56px]">
      <div className="relative">
        <Icon className="size-6 text-black" strokeWidth={1.5} />
        {currentButton.showCount && count != null && count > 0 && (
          <span className="absolute -top-2.5 -right-4 text-[11px] font-semibold text-black leading-none">
            {count}
          </span>
        )}
      </div>
      <span className="text-center text-black text-xs font-medium">
        {currentButton.title}
      </span>
    </div>
  );

  if (currentButton.onClick) {
    return (
      <button type="button" onClick={currentButton.onClick} className="flex">
        {content}
      </button>
    );
  }

  return (
    <Link href={currentButton.href as string} className="flex">
      {content}
    </Link>
  );
}